/**
 * TODO: Retro-fit for conditional PlayCanvas/Zappier Integration
 */
import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
// @ts-ignore
import { imageClassifier } from 'ml5';
import type { GoToPage } from '@tmsw/powerups.config.page.provider';
import { CTAButton } from '@tmsw/powerups.ui.buttons.cta-button';
import { usePageConfig } from '@tmsw/powerups.config.page.provider';
import { useStore } from '@tmsw/powerups.store';
import { useTranslation } from '@tmsw/powerups.intl.hooks.use-translation';
import { usePrevious } from '@tmsw/powerups.hooks.use-previous';
import { Loader } from '@tmsw/powerups.ui.loaders.base';
import { usePauseConditions } from '../../hooks/usePauseConditions';
import { Snackbar } from '../../components/Snackbar';
import ToyScanVideoElement from './ToyScanVideoElement';
import CardFrame from './CardFrame';
import './ToyScan.css';
import CardIcon from './CardIcon';

type MlClassifierModel = {
  video: any;
  model: string; // JSON.strigifed data
  mapStringToIndex: string[]; // character slugs (UPPERCASE)
  modelUrl: string; // url relative to public/ => "toyscan-models/us_ca_model/model.json"
  ready: Promise<boolean> | boolean;
  [key: string]: any;
};

type ClassifierResult = {
  label: string;
  confidence: number;
};

export type ToyScanPageProps = {
  goToPage: GoToPage;
};

export const ToyScan = ({ goToPage }: ToyScanPageProps) => {
  // defs
  // const parentNodeCharacterSelection = 'CHARACTER_SELECTION';
  // const parentNodeRecognition = 'RECOGNITION_TOY';
  const TOY_SCAN_DELAY = 3000;
  const modelUrl = './static/media/global/toyscan-models/model.json';

  // hooks
  const { __t } = useTranslation();
  const { pagesMap } = usePageConfig();
  const externalPauseTriggered = usePauseConditions()();

  // global state
  const viewport = useStore((state) => state.viewport);
  // const cameraAccessConsented = useStore(
  //   (state) => state.cameraAccessConsented
  // );
  const gameCharacter = useStore((state) => state.gameCharacter);
  const setGameCharacter = useStore(
    useCallback((state) => state.setGameCharacter, [])
  );
  // const setSelectedVariant = useStore(
  //   useCallback((state) => state.setSelectedVariant, [])
  // );

  // local state
  //const [toyResult, setToyResult] = useState(null);
  const [toyResult, setToyResult] = useState<string | null>(null);

  // const [showTroublePrompt, setShowTroublePrompt] = useState(false);
  const [scannerReady, setScannerReady] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [userMediaError, setUserMediaError] = useState(null);
  // const [currentScanResult, setCurrentScanResult] = useState<
  //   ClassifierResult[]
  // >([]);
  const scanSampleSize = 16;
  const setPageTitle = useStore((state) => state.setPageTitle);
  // const showSettingsPanel = useStore((state) => state.showSettingsPanel);
  // const inPortrait = useStore((state) => state.inPortrait);
  // const isPauseEnabled = useStore((state) => state.isPauseEnabled);

  // translated copy
  const characterSelectionCopy = __t('CHARACTER_SELECTION');
  const scannerCopy = __t('CARD_SCAN');
  const charactersList = characterSelectionCopy.characters;
  console.log('scannerCopy', scannerCopy);
  const aimCameraSnackbarRef: MutableRefObject<HTMLDivElement | null> =
    useRef(null);

  // refs media elements
  const videoRef: MutableRefObject<HTMLVideoElement | null> = useRef(null);
  const canvasRef: MutableRefObject<HTMLCanvasElement | null> = useRef(null);

  // ref to manage  the toy model loading state
  const modelLoadingRef: MutableRefObject<boolean> = useRef(false);

  // ref to rAF for scan interval
  const rafRef: MutableRefObject<number | null> = useRef(null);

  // ref to the current model url from config
  const mlModelUrlRef: MutableRefObject<string> = useRef(modelUrl);

  // ref to the classifier model
  const classifierRef: MutableRefObject<MlClassifierModel | null> =
    useRef(null);

  let lastTickClassifier: MlClassifierModel | null | undefined = usePrevious(
    classifierRef.current
  );

  const characters = __t('characters', 'CHARACTER_SELECTION');
  const lookupCharacter = (slug: string) =>
    characters.find((c: LocaleJson.Character) => c.slug === slug);

  const scanResultSamples = useRef([]);

  /**
   * Purge the video stream from camera
   * @returns {void}
   */
  const showSettingsPanel = useStore((state) => state.showSettingsPanel);
  const isPauseEnabled = useStore((state) => state.isPauseEnabled);
  const inPortrait = useStore((state) => state.inPortrait);

  useEffect(() => {
    setPageTitle(__t('pageTitle', 'CARD_SCAN'));
  }, [showSettingsPanel, isPauseEnabled, inPortrait]);

  const purgeVideo = useCallback((video: HTMLVideoElement | null) => {
    console.log('PURGING VIDEO!', video);
    if (!video) {
      console.log('videoRef not found!');
      return;
    }
    console.log('Found videoRef ', video);
    if (video?.srcObject instanceof MediaStream) {
      video.srcObject.getTracks().forEach((track) => track.stop());
      video.pause();
      if (video.src) video.src = '';
      video.removeAttribute('src'); // empty source
      video.load();
    }
  }, []);

  // async function startVideoStream
  const startVideoStream = useCallback(async () => {
    console.log('Starting Video Stream');
    if (!videoRef.current) return false;

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error('getUserMedia is not supported in this browser');
    }

    try {
      videoRef.current.srcObject = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { exact: 'environment' } },
        audio: false,
      });
    } catch (e) {
      throw e;
    }

    try {
      if (
        videoRef.current.paused ||
        videoRef.current.dataset.playlock === 'unlocked'
      ) {
        videoRef.current.setAttribute('data-playlock', 'locked');
        videoRef.current.play().then((__) => {
          videoRef.current!.setAttribute('data-Playlock', 'unlocked');
        });
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  /**
   * Stops the scanners
   */
  const stopScanner = useCallback(
    ({ video }: { video: HTMLVideoElement | null }) => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      purgeVideo(video);
      purgeVideo(videoRef.current);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /**
   * Starts the scanner
   * @return Promise<intervalID>
   */
  const startScanner = useCallback(
    async function startScanner() {
      console.log('Starting Scanner!!!!!');
      if (!canvasRef.current || !videoRef.current) return;

      // lock the player down to escape double hok hell
      if (videoRef.current.dataset.playlock === 'locked') return;
      videoRef.current.setAttribute('data-playlock', 'locked');

      // start the camera stream
      try {
        await startVideoStream();
      } catch (e: any) {
        console.log('userMediaError', e);
        setUserMediaError(e);
      }

      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      const video = videoRef.current;

      // JavaScript program to find the most frequent element in an array
      // const mostFrequent = (arr: any[]) => {
      //   let maxcount = 0;
      //   let element_having_max_freq;
      //   for (let i = 0; i < arr.length; i++) {
      //     let count = 0;
      //     for (let j = 0; j < arr.length; j++) {
      //       if (arr[i] === arr[j]) count++;
      //     }

      //     if (count > maxcount) {
      //       maxcount = count;
      //       element_having_max_freq = arr[i];
      //     }
      //   }

      //   return {
      //     label: element_having_max_freq,
      //     percentage: maxcount / arr.length,
      //   };
      // };

      const runClassifier = () => {
        let newCharacter: LocaleJson.Character;
        if (!canvas || !context || !video) return;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        classifierRef.current!.classify(
          canvas,
          (err: any, results: ClassifierResult[]) => {
            if (err) {
              setScanError(err);
              console.error('SCAN ERROR! ', err);
            } else {
              // if (results && results.length > 0) {
              //   setCurrentScanResult(results[0]);
              // }

              let newToyResult = null;
              if (results.length > 0) {
                results = results.filter((r) => r.label !== 'Environment');
              }
              console.log('results', results);
              //const newToyResult = results.length > 0 && results[0].confidence > (results[0].label == "kulikitakacat_2" ? 0.9999999999999999999999 : 0.985) ? results[0].label.toLocaleLowerCase() : null;
              if (
                results.length > 0 &&
                results[0].confidence >
                  0.9 /*(results[0].label == "kulikitakacat_2" ? 0.9999 : 0.84)*/
              ) {
                // if (scanResultSamples.current.length >= scanSampleSize)
                //   scanResultSamples.current.pop();
                // // @ts-ignore
                // scanResultSamples.current.unshift(results[0].label);
                // const currentPrediction = mostFrequent(
                //   scanResultSamples.current
                // );
                newToyResult = results[0].label;
              }

              console.log('newToyResult', newToyResult);

              if (newToyResult !== null && newToyResult !== 'Environment') {
                const [characterName, variant] = newToyResult.split('_');
                console.log(
                  'newToyResult=',
                  newToyResult,
                  'characterName=',
                  characterName,
                  'variant=',
                  variant
                );
                setToyResult(newToyResult);
                newCharacter = lookupCharacter(characterName);
                if (newCharacter) {
                  stopScanner({ video: videoRef.current });
                  setGameCharacter(characterName.toLocaleLowerCase());
                  setTimeout(() => {
                    goToPage('SCAN_CONFIRMATION');
                  }, 1000);
                }
              }
            }
            if (!newCharacter) {
              rafRef.current = requestAnimationFrame(runClassifier);
            }
          }
        );
      };

      setScannerReady(true);

      setTimeout(() => {
        rafRef.current = requestAnimationFrame(runClassifier);
      }, TOY_SCAN_DELAY);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [startVideoStream, stopScanner]
  );

  const cleanExit = useCallback(
    (targetPage: string | number) => {
      setUserMediaError(null);
      setScanError(null);
      stopScanner({ video: videoRef.current });
      setTimeout(() => {
        goToPage(targetPage);
      }, 1000);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [goToPage, stopScanner]
  );

  const exitToCharacterSelectionPage = useCallback(() => {
    cleanExit('CHARACTER_SELECTION');
  }, [cleanExit]);

  // mount side effects
  useEffect(() => {
    setUserMediaError(null);
    setScanError(null);
    setGameCharacter(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPageTitle(__t('pageTitle', 'CARD_SCAN'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalPauseTriggered]);

  const [hideFrame, setHideFrame] = useState(false);
  useEffect(() => {
    if (scannerReady) {
      const troubleScanningTimeout = setTimeout(() => {
        document
          .querySelector('.snackbar.snackbar--scan-trouble')
          ?.classList.remove('snackbar--hidden');
        setHideFrame(true);
        if (aimCameraSnackbarRef.current !== null) {
          aimCameraSnackbarRef.current.classList.add('snackbar--hidden');
        }
        //document.querySelector(".cta-btn.cta-btn--scan-trouble")?.focus();
      }, 5000);

      return () => {
        clearTimeout(troubleScanningTimeout);
      };
    }
  }, [scannerReady]);

  /**
   * Sync canvas size to viewport size on change
   */
  useEffect(() => {
    const updateCanvasSize = () => {
      if (!canvasRef.current) return;
      canvasRef.current.width = viewport.width;
      canvasRef.current.height = viewport.height;
    };
    updateCanvasSize();
  }, [viewport]);

  /**
   * Only reload the classifier when the modal changes
   */
  /**
   * Only reload the classifier when the modal changes
   */
  useEffect(() => {
    if (modelLoadingRef.current) return;
    modelLoadingRef.current = true;
    if (
      mlModelUrlRef.current === null ||
      mlModelUrlRef.current === lastTickClassifier?.modelUrl
    )
      return;
    imageClassifier(mlModelUrlRef.current, (err: any, classifier: any) => {
      modelLoadingRef.current = false;
      if (err) throw err;
      classifierRef.current = classifier;
      console.log('Model loaded', classifierRef.current);
      mlModelUrlRef.current = classifierRef.current!.modelUrl;
    });
    // @ts-ignore
  }, [lastTickClassifier?.modelUrl]);

  /**
   * Start scanning on mount; Stop scanning on dismount
   */
  useEffect(() => {
    if (!rafRef.current) {
      startScanner();
    }
    const video = videoRef.current;
    return () => {
      stopScanner({ video });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Handle prediction results
   */
  useEffect(() => {
    if (toyResult !== null && !gameCharacter) {
      console.log('Game character updated to:', toyResult);
      setGameCharacter(toyResult);
      cleanExit('SCAN_CONFIRMATION');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setGameCharacter, cleanExit, toyResult]);

  /**
   * Exit to level selection on error
   */
  useEffect(() => {
    if (userMediaError || scanError) {
      console.log('Error', userMediaError, scanError);
      stopScanner({ video: videoRef.current });
      setTimeout(() => {
        stopScanner({ video: videoRef.current });
        exitToCharacterSelectionPage();
      }, 2500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanError, userMediaError]);

  return (
    <div>
      <div className="scanner">
        {!scannerReady && <Loader text={__t('loading', 'GENERAL')} />}
        {scannerReady && (
          <>
            <div id="scanPreview">
              {!hideFrame ? <CardFrame className="card-frame" /> : null}
              {/*Enable to get a visible update of the current detection*/}
              {/*{currentScanResult && (<h2>{currentScanResult.label + " : " + Number(currentScanResult.confidence * 100).toFixed(1) + "%"} </h2>)}*/}
            </div>
            <div
              role="alert"
              aria-live="assertive"
              className="scan-copy-trouble"
            >
              <div className="scan-trouble-wrapper">
                <Snackbar
                  ref={null}
                  role="alert"
                  className="snackbar--scan-trouble snackbar--hidden"
                >
                  <p
                    role="alert"
                    aria-live="assertive"
                    aria-label={scannerCopy?.scanProblemCopy.replaceAll(
                      '\\n',
                      `\n`
                    )}
                    style={{
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {scannerCopy?.scanProblemCopy}
                  </p>
                  <CTAButton
                    primary
                    intlKey={'buttons.next'}
                    parentNode={'GENERAL'}
                    className="cta-btn--scan-trouble"
                    onClick={exitToCharacterSelectionPage}
                    style={{ zIndex: '2', width: '50%' }}
                  />
                </Snackbar>
              </div>
            </div>
            <div className="scan-copy-wrapper">
              <Snackbar
                ref={aimCameraSnackbarRef}
                className="scan-dialogue-box"
              >
                <CardIcon className="card-icon" />
                <p className="scan-dialogue-text">{scannerCopy?.h1}</p>
              </Snackbar>
            </div>
          </>
        )}
        <ToyScanVideoElement ref={videoRef} />
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>
    </div>
  );
};
